import {
  BookOpen,
  CheckSquare,
  Code,
  ExternalLink,
  FileText,
  Folder,
  HelpCircle,
  Lightbulb,
  type LucideIcon,
  MessageSquare,
  Server,
  Video,
} from 'lucide-react'
import type { SourceContentItemType } from '@/components/admin/sources/types'
import type { CatalogLearningItemKind } from '@/lib/coaching/learning-item-projection'
import { normalizeKind } from '@/lib/source-content'

/**
 * The shared vocabulary of which mark stands for which kind of thing. Owned
 * once here rather than restated per feature area: containers and leaves keep
 * separate tables because they answer different questions, but both resolve
 * through `normalizeKind` so casing and stray whitespace never fork the
 * answer.
 */

/**
 * `SourceContent.kind` is load-bearing free text (`db/schema/content.ts:122`),
 * so no map can be exhaustive — these are the kinds actually in use, and
 * anything else gets the generic folder.
 */
const CONTAINER_ICONS: Record<string, LucideIcon | undefined> = {
  chapter: BookOpen,
  section: Folder,
  quiz: HelpCircle,
}

/**
 * The fallback mark for a container's free-text kind, used wherever a
 * container row renders without an authored icon, so a chapter, a quiz, and a
 * section don't all collapse to an identical folder.
 *
 * It lives here rather than beside either caller because both the curriculum
 * library tree and the source editor need it, and they sit in different
 * feature areas — neither should have to reach into the other.
 */
export function containerFallbackIcon(kind: string): LucideIcon {
  return CONTAINER_ICONS[normalizeKind(kind)] ?? Folder
}

/**
 * Leaf kinds reach us in two spellings — the catalog's snake_case
 * `CatalogLearningItemKind` and the source editor's SCREAMING
 * `SourceContentItemType` — which `normalizeKind` collapses onto one key, so
 * `Lowercase<SourceContentItemType>` makes this `Record` exhaustive over both.
 * Adding a kind to either enum is then a type error here, rather than silent
 * drift between two feature areas that each assumed the other agreed with it.
 */
type LeafKind = CatalogLearningItemKind | Lowercase<SourceContentItemType>

const LEAF_ICONS: Record<LeafKind, LucideIcon> = {
  problem: Code,
  coding_exercise: Lightbulb,
  system_design: Server,
  task: CheckSquare,
  resource: ExternalLink,
  source_content: BookOpen,
  behavioral: MessageSquare,
  video: Video,
  article: FileText,
}

// Widened for lookup: `kind` is a runtime string off a DB column or an API
// payload, so it can't be assumed to be a `LeafKind` even where the calling
// signature says otherwise.
const LEAF_ICON_LOOKUP: Record<string, LucideIcon | undefined> = LEAF_ICONS

/**
 * The mark for a leaf item's kind — a problem, a task, a linked resource.
 * An unrecognized kind gets the generic document mark rather than nothing, so
 * a row whose kind lands ahead of this table still renders.
 */
export function leafKindIcon(kind: string): LucideIcon {
  return LEAF_ICON_LOOKUP[normalizeKind(kind)] ?? FileText
}
